/**
 * AllowUnlockSection
 *
 * Toggles whether non-subscribers can unlock the exclusive video, and
 * captures the USD price (1.00–100.00).
 * Mirrors Flutter `_allowContentUnlockWidget` from
 * content_monetization_screen.dart.
 */

import React from 'react';
import { useTheme } from '@/presentation/contexts/ThemeContext';
import { useResponsiveDesign } from '@/presentation/responsiveDesign/responsiveDesign';
import { useLocale } from '@/presentation/contexts/LocaleContext';
import { AssetIcon } from '@/presentation/components/common/icon/AssetIcon';
import { ToggleSwitch } from '@/presentation/components/common/controls/ToggleSwitch';
import { qaAttr } from '@/presentation/utils/qaAttr';
import { MathUtils } from '@/domain/utils/mathUtils';
import { remoteConfigService } from '@/domain/services/remoteConfig/remoteConfigService';

interface AllowUnlockSectionProps {
  enabled: boolean;
  inputText: string;
  unlockChargeUSD: number | null;
  onToggle: (value: boolean) => void;
  onChangeText: (text: string) => void;
  onFocus: () => void;
  onBlur: () => void;
}

export const AllowUnlockSection: React.FC<AllowUnlockSectionProps> = ({
  enabled,
  inputText,
  unlockChargeUSD,
  onToggle,
  onChangeText,
  onFocus,
  onBlur,
}) => {
  const { theme } = useTheme();
  const responsive = useResponsiveDesign();
  const { l10n } = useLocale();

  const description = l10n.publish.allowVideoUnlockDescription
    .replace('{min}', MathUtils.removeTrailingZero(remoteConfigService.contentUnlockMinDollars))
    .replace('{max}', MathUtils.removeTrailingZero(remoteConfigService.contentUnlockMaxDollars))
    .replace('{currency}', remoteConfigService.defaultCurrency);

  const earningsText =
    unlockChargeUSD != null
      ? l10n.publish.coinsWillBeAddedPerUnlock.replace(
        '{coins}',
        MathUtils.removeTrailingZero(MathUtils.coinsFromUSD(unlockChargeUSD))
      )
      : null;

  const inputBackground = enabled ? theme.secondary : theme.secondary.concat('80'); // 50% opacity when disabled

  return (
    <div style={{ display: 'flex', flexDirection: 'column' }}>
      <div style={{ display: 'flex', alignItems: 'center', gap: responsive.spacing.xs }}>
        <AssetIcon
          path="/assets/images/locked.png"
          width={responsive.icon.small}
          height={responsive.icon.small}
          color={theme.disabled}
        />
        <span
          style={{
            flex: 1,
            fontSize: responsive.font.base,
            fontWeight: responsive.fontWeight.semibold,
            color: theme.text.primary,
          }}
        >
          {l10n.publish.allowVideoUnlock}
        </span>
        <ToggleSwitch
          value={enabled}
          onChange={onToggle}
          qa={{ id: 'monetization-allow-unlock', value: enabled ? 'on' : 'off' }}
        />
      </div>

      <div
        style={{
          marginTop: responsive.spacing.sm,
          backgroundColor: inputBackground,
          borderRadius: responsive.borderRadius.base,
          padding: `${responsive.spacing.xs}px ${responsive.spacing.lg}px`,
        }}
      >
        <input
          {...qaAttr({ id: 'monetization-unlock-price' })}
          type="text"
          inputMode="decimal"
          value={inputText}
          onFocus={onFocus}
          onBlur={onBlur}
          onChange={(e) => onChangeText(e.target.value)}
          disabled={!enabled}
          placeholder={l10n.publish.enterAmount}
          style={{
            width: '100%',
            background: 'transparent',
            border: 'none',
            outline: 'none',
            color: enabled ? theme.text.primary : theme.text.secondary,
            fontSize: responsive.font.lg,
            fontWeight: responsive.fontWeight.semibold,
            padding: 0,
          }}
        />
      </div>

      <div
        style={{
          fontSize: responsive.font.sm,
          color: theme.text.secondary,
          fontWeight: responsive.fontWeight.medium,
          marginTop: responsive.spacing.sm,
        }}
      >
        {description}
      </div>

      {enabled && earningsText && (
        <div
          style={{
            fontSize: responsive.font.sm,
            color: theme.text.primary,
            marginTop: responsive.spacing.lg,
          }}
        >
          {earningsText}
        </div>
      )}
    </div>
  );
};
